import { useTimeCapsuleInvites, useTimeCapsuleMembers, useUser } from "api/ApiHooks"
import { LoversApi } from "api/LoversApi"
import { Button, Input } from "components/ui"
import { useState } from "react"

export const TimeCapsuleCollaboratorsForm = (props: {
  timeCapsuleId: string
  onSave?: (name: string, opensAt: Date) => void
}) => {
  const existingInvites = useTimeCapsuleInvites(props.timeCapsuleId)
  const members = useTimeCapsuleMembers(props.timeCapsuleId)
  const { data: userData } = useUser()

  const [emails, setEmails] = useState<string[]>([])
  const [inputEmail, setInputEmail] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAddEmail = () => {
    if (!inputEmail) return
    const existingPendingInvites =
      existingInvites.data?.invitations.filter((invite) => invite.status === "pending") ||
      []

    if (!inputEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError("Please enter a valid email address")
      return
    }

    if (emails.map((email) => email.toLowerCase()).includes(inputEmail.toLowerCase())) {
      setError("This email has already been added")
      return
    }

    if (userData?.user.email.toLowerCase() === inputEmail.toLowerCase()) {
      setError("You cannot invite yourself but you should love yourself ❤️")
      return
    }

    if (
      existingPendingInvites.some(
        (invite) => invite.email.toLowerCase() === inputEmail.toLowerCase()
      )
    ) {
      setError("This email has already been invited")
      return
    }

    if (
      members.data?.members.some(
        (member) => member.email.toLowerCase() === inputEmail.toLowerCase()
      )
    ) {
      setError("This person is already a collaborator")
      return
    }

    setEmails([...emails, inputEmail])
    setInputEmail("")
    setError(null)
  }

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails(emails.filter((email) => email !== emailToRemove))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (emails.length === 0) {
      setError("Please add at least one email address")
      return
    }

    try {
      setIsSubmitting(true)
      await LoversApi().timeCapsule.createTimeCapsuleInvitations(props.timeCapsuleId, {
        emails
      })
      // Optional: Clear form after successful submission
      existingInvites.refetch()
      setEmails([])
      setError(null)
    } catch (err) {
      setError("Failed to send invitations. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 border-t border-gray-900/10 dark:border-white/10 py-10">
      <form onSubmit={handleSubmit}>
        <div className="space-y-12">
          <div>
            <h2 className="font-semibold text-lg text-gray-900 dark:text-white">
              Collaborators
            </h2>
            <p className="mt-1 text-sm/6 text-gray-600 dark:text-gray-300">
              Invite collaborators to your time capsule. They'll be able to add entries
              and view the time capsule when it opens.
            </p>

            <div className="flex flex-col-reverse gap-10 md:flex-row md:gap-2 mt-10">
              <div className="flex flex-1 flex-col">
                <ExistingInvites
                  invites={existingInvites.data?.invitations || []}
                  isLoading={existingInvites.isLoading}
                  hasError={existingInvites.isError}
                  refecthInvites={existingInvites.refetch}
                />
              </div>
              <div className="flex flex-1 flex-col">
                <ExistingCollaborators
                  members={members.data?.members || []}
                  isLoading={members.isLoading}
                  hasError={members.isError}
                  refetchMembers={members.refetch}
                />
              </div>
            </div>

            <div className="mt-6">
              <h3 className="font-semibold text-base text-gray-900 dark:text-white mb-2">
                Invite collaborators
              </h3>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={inputEmail}
                  onChange={(e) => {
                    setError(null)
                    setInputEmail(e.target.value)
                  }}
                  placeholder="Enter email address"
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleAddEmail()
                    }
                  }}
                />
                <Button onClick={handleAddEmail}>Add</Button>
              </div>

              {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

              <div className="mt-4">
                {emails.map((email) => (
                  <div key={email} className="flex items-center gap-2 mt-2">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {email}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(email)}
                      className="text-sm text-red-600 hover:text-red-500"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <Button
                type="submit"
                isLoading={isSubmitting}
                disabled={emails.length === 0}
              >
                {isSubmitting ? "Sending invitations..." : "Send invitations"}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

const ExistingInvites = (props: {
  invites: { id: string; email: string; status: string }[]
  isLoading: boolean
  hasError: boolean
  refecthInvites: () => void
}) => {
  const { invites, isLoading, hasError, refecthInvites } = props
  const pendingInvites = invites.filter((invite) => invite.status === "pending")

  const handleDelete = (id: string) => {
    LoversApi()
      .invitations.deleteInvitation(id)
      .then(() => {
        refecthInvites()
      })
  }

  return (
    <>
      <h3 className="font-semibold text-base text-gray-900 dark:text-white mb-2">
        Pending invitations
      </h3>
      {hasError && (
        <p className="text-sm text-red-600">Failed to load pending invitations</p>
      )}

      {isLoading ? (
        <p>Loading...</p>
      ) : pendingInvites.length > 0 ? (
        <ul className="space-y-2">
          {pendingInvites.map((invite) => (
            <li key={invite.id} className="flex items-center gap-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {invite.email}
              </span>
              <button
                type="button"
                onClick={() => handleDelete(invite.id)}
                className="text-sm text-red-600 hover:underline"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Currently no pending invitations
        </p>
      )}
    </>
  )
}

const ExistingCollaborators = (props: {
  members: {
    membershipId: string
    id: string
    name: string
    email: string
    memberSince: string
  }[]
  isLoading: boolean
  hasError: boolean
  refetchMembers: () => void
}) => {
  const { members, isLoading, hasError, refetchMembers } = props
  const [willDelete, setWillDelete] = useState<string | null>(null)

  const tryDeletion = (id: string) => {
    if (willDelete === id) {
      handleDelete(id)
      setWillDelete(null)
      return
    }

    setWillDelete(id)
    setTimeout(() => {
      setWillDelete(null)
    }, 3000)
  }

  const handleDelete = (id: string) => {
    LoversApi()
      .members.deleteMember(id)
      .then(() => {
        refetchMembers()
      })
  }

  return (
    <>
      <h3 className="font-semibold text-base text-gray-900 dark:text-white mb-2">
        Collaborators
      </h3>
      {hasError && <p className="text-sm text-red-600">Failed to load collaborators</p>}

      {isLoading ? (
        <p>Loading...</p>
      ) : members.length > 0 ? (
        <ul className="space-y-2">
          {members.map((member) => (
            <li
              key={member.membershipId}
              className="flex justify-between items-center gap-5 border rounded-md p-2"
            >
              <div className="flex flex-col">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {member.name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {member.email}
                </span>
              </div>
              <button
                type="button"
                onClick={() => tryDeletion(member.membershipId)}
                className="text-xs bg-red-500 text-white px-2 py-1 rounded-md hover:bg-red-600"
              >
                {willDelete === member.membershipId ? "Are you sure?" : "Remove"}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-700 dark:text-gray-300">No collaborators yet</p>
      )}
    </>
  )
}
